import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@material-ui/core';
import Button from '../../atoms/Button';
import Textbox from '../../atoms/Textbox';
import ButtonGroup from '../../atoms/ButtonGroup';
import Modal from '../../molecules/Modal';
import LabelWithSymbols from '../../atoms/LabelBySymbols';
import { AppState } from '../../../redux/modules/store';
import { PERMISSION_MODAL_TYPE, PermissionModalType, PERMISSION_TYPE, PermissionType } from '../../../Common';
import useApplicationVariableList from '../../../utils/CustomHooks/useApplicationVariableList';
import { MESSAGE_MASTER_KEYS, REFERENCE_CODE, getMessageFromMasterInfo } from '../../../redux/reducers/MasterInfoReducer';
import { callCrewNavi, callSoftbankOfficial } from '../../../external/GinieBrowser/SelectTabPage';

export interface PermissionModalProps {
  isOpen: boolean;
  modalType: PermissionModalType;
  permittedFlag: boolean;
  selectedPermissionType?: string;
  onClose: () => void; // 否定する時に実行するハンドラー
  onAccept: (type: string, memo: string) => void; // 肯定する時に実行するハンドラー
}

/**
 * 情報更新・情報保持許諾ダイアログ
 */
const PermissionModal: React.FC<PermissionModalProps> = ({
  isOpen,
  modalType,
  permittedFlag,
  selectedPermissionType,
  onClose,
  onAccept,
}) => {
  const [memo, setMemo] = useState('');
  const [permissionType, setPermissionType] = useState<PermissionType>(PERMISSION_TYPE.FACE_TO_FACE);
  const masterInfo = useSelector((state: AppState) => state.masterInfo);
  const { getApplicationVariableByReferenceCd } = useApplicationVariableList();

  const sbPrivacyPolicy = getApplicationVariableByReferenceCd(REFERENCE_CODE.STR_U_0032) as string[];

  // クルーナビ(お客さま情報の取り扱いについて)URLのパス取得
  const crewNavi = getApplicationVariableByReferenceCd(REFERENCE_CODE.STR_U_0024) as string[];

  // 承認ボタンが押下された時の制御
  const onAcceptContoller = () => {
    onAccept(permissionType, memo);
  };

  // モーダル表示フラグ取得;許諾種類(情報保持許諾・情報更新)ごとにそれぞれの承諾状態から表示・非表示を決める。
  // 情報保持許諾の場合、無条件で表示(お客さま情報登録でしか表示しないため)
  // 情報更新許諾の場合、顧客ID変わるまたは承諾されてない場合に表示(初回表示でなくても、許諾タイプにより承諾されないため)
  const modalVisibleFlg = modalType === PERMISSION_MODAL_TYPE.INFORMATION_EDIT ? !permittedFlag : true;
  if (isOpen) {
    // 情報更新許諾について既に承諾された場合、モーダル表示せず画面遷移(承認時の動き)へ遷移される
    if (!modalVisibleFlg) {
      onAccept(selectedPermissionType ? selectedPermissionType : permissionType, memo);
    }
  }

  // 現在許諾モーダル情報
  const currentModalInfo = MODAL_INFOS.find((modal) => modal.type === modalType) || MODAL_INFOS[0];
  // 現在許諾モーダルに該当する許諾タイプ(対面・非対面など)情報
  const permissionTypeInfos = PERMISSION_TYPE_INFOS.filter((_form) =>
    currentModalInfo.permissionTypes.some((_type) => _type === _form.type)
  );
  // 現在選択許諾の情報
  const currentPermissionTypeInfos = permissionTypeInfos.find((_form) => _form.type === permissionType) || permissionTypeInfos[0];

  let permissionMessageId = '';
  if (modalType === PERMISSION_MODAL_TYPE.INFORMATION_EDIT) {
    permissionMessageId = currentPermissionTypeInfos.permissionMessageId;
  } else {
    permissionMessageId = permissionType === PERMISSION_TYPE.NON_FACE_TO_FACE ? MESSAGE_MASTER_KEYS.M_0022 : MESSAGE_MASTER_KEYS.M_0023;
  }
  const permissionMessage = getMessageFromMasterInfo(masterInfo, permissionMessageId);

  return (
    <Modal
      open={isOpen && modalVisibleFlg}
      hasFade={false}
      onBackdropClick={onClose}
      onEscapeKeyDown={onClose}
      surfaceClassName={`u-mv-auto ${modalType === PERMISSION_MODAL_TYPE.INFORMATION_RETENTION ? 'u-w4l' : ''}`}
      data-testid='modal'>
      <Modal.Header hasCloseButton={false} data-testid='permission-modal--title' data-testid-e2e='permissionModal-title'>
        {currentModalInfo.title}
      </Modal.Header>
      <Modal.Content className='u-p16'>
        <ButtonGroup className='u-mb4' width='full' data-testid='permission-modal--type-buttons'>
          {
            // オプトイン対応時、その他非表示対応(戻せるように非表示)
            permissionTypeInfos
              .filter((form) => form.type !== PERMISSION_TYPE.OTHER)
              .map(({ type, name }) => (
                <ButtonGroup.Button
                  key={type}
                  isActive={permissionType === type}
                  onClick={() => setPermissionType(type)}
                  label={name}
                  data-testid={`permission-modal--type-button-${type}`}
                />
              ))
          }
        </ButtonGroup>
        {currentPermissionTypeInfos.isShowMemo && (
          <Textbox
            className='u-mb16'
            placeholder='詳細を記入'
            value={memo}
            onChange={(e) => {
              setMemo(e.target.value);
            }}
            width='full'
          />
        )}
        {currentPermissionTypeInfos.type !== PERMISSION_TYPE.EDIT_INFO && (
          <p className='u-flex u-text-danger u-mb4' data-testid='permission-modal--warning-Text'>
            <Icon className='u-text-middle u-mr8'>error</Icon>
            {currentPermissionTypeInfos.warningText}
          </p>
        )}
        <LabelWithSymbols text={permissionMessage} data-testid='permission-modal--confilm-content' />
        <Button
          className='u-mr24'
          type='transparent'
          label={<span className='u-text-underline'>クルー用注意事項（クルーナビ）</span>}
          rightIcon='launch'
          onClick={() => callCrewNavi(crewNavi.length ? crewNavi[0] : '')}
          data-testid='permission-modal--crew-navi'
        />
        <Button
          type='transparent'
          label={<span className='u-text-underline'>ソフトバンクのプライバシーポリシー</span>}
          rightIcon='launch'
          onClick={() => callSoftbankOfficial(sbPrivacyPolicy.length ? sbPrivacyPolicy[0] : '')}
          data-testid='permission-modal--privacy-policy'
        />
      </Modal.Content>
      <Modal.Actions>
        <Button
          label='キャンセル'
          type='secondary'
          onClick={onClose}
          data-testid='permission-modal--cancel-button'
          data-testid-e2e='permissionModal-cancelButton'
        />
        <Button
          label={currentPermissionTypeInfos.acceptButtonText}
          onClick={onAcceptContoller}
          data-testid='permission-modal--accept-button'
          data-testid-e2e='permissionModal-acceptButton'
        />
      </Modal.Actions>
    </Modal>
  );
};

// 許諾モーダルタイプ(情報保持許諾/情報更新)別情報
const MODAL_INFOS = [
  {
    type: PERMISSION_MODAL_TYPE.INFORMATION_RETENTION,
    title: '情報保持許諾',
    // 該当許諾モーダルに表示する許諾種類記載
    permissionTypes: [PERMISSION_TYPE.FACE_TO_FACE, PERMISSION_TYPE.NON_FACE_TO_FACE],
  },
  {
    type: PERMISSION_MODAL_TYPE.INFORMATION_EDIT,
    title: '情報更新',
    permissionTypes: [PERMISSION_TYPE.FACE_TO_FACE, PERMISSION_TYPE.NON_FACE_TO_FACE, PERMISSION_TYPE.EDIT_INFO, PERMISSION_TYPE.OTHER],
  },
];

// 許諾種類別情報
const PERMISSION_TYPE_INFOS = [
  {
    type: PERMISSION_TYPE.FACE_TO_FACE,
    name: '対面',
    isForUpdate: false,
    // ストアの承諾情報を更新する許諾かを定義 > true:更新する。
    isForConsentInfoUpdate: true,
    warningText: '必ずお客さまがご確認ください。',
    permissionMessageId: MESSAGE_MASTER_KEYS.M_0012,
    acceptButtonText: '承諾します',
    isShowMemo: false,
  },
  {
    type: PERMISSION_TYPE.NON_FACE_TO_FACE,
    name: '非対面',
    isForUpdate: false,
    isForConsentInfoUpdate: true,
    warningText: 'お客様に口頭で説明し、クルーが代理で承諾ボタンを押してください。',
    permissionMessageId: MESSAGE_MASTER_KEYS.M_0013,
    acceptButtonText: '代理承諾',
    isShowMemo: false,
  },
  {
    type: PERMISSION_TYPE.EDIT_INFO,
    name: '情報修正',
    isForUpdate: true,
    isForConsentInfoUpdate: false,
    warningText: '新しく取得する情報は、以下承諾を顧客から継続的に得たものとして更新します。',
    permissionMessageId: MESSAGE_MASTER_KEYS.M_0014,
    acceptButtonText: '代理承諾',
    isShowMemo: false,
  },
  {
    type: PERMISSION_TYPE.OTHER,
    name: 'その他',
    isForUpdate: true,
    isForConsentInfoUpdate: false,
    warningText: '新しく取得する情報は、以下承諾を顧客から継続的に得たものとして更新します。',
    permissionMessageId: MESSAGE_MASTER_KEYS.M_0014,
    acceptButtonText: '代理承諾',
    // オプトイン対応時、その他非表示対応(戻せるように非表示)
    isShowMemo: false,
  },
];

export default PermissionModal;
